/**
 * Backup & recovery (§3.2): one-click export, previewed import, FS-Access
 * auto-export, and the 14-day nag. Written for a user who has never opened
 * a developer tool.
 */
import { collectBundle, restoreBundle, getDb, getSettings, putSettings, putSettingsRaw, type Bundle } from './db';
import { nowIso } from './ids';
import { SCHEMA_VERSION } from './types';
import { convertLegacy, isLegacyFile } from '../data/importLegacy';

export const EXPORT_NAG_DAYS = 14;

export async function buildExportJson(): Promise<string> {
  const bundle = await collectBundle({ includePhotoBytes: true });
  return JSON.stringify(bundle);
}

export function exportFilename(now = new Date()): string {
  const d = now.toISOString().slice(0, 10);
  return `forever-green-backup-${d}.json`;
}

export async function markExported(): Promise<void> {
  const s = await getSettings();
  // lastExportAt is machine-owned, so this has to go through the raw writer;
  // putSettings would take the stored value back and the nag would never clear
  await putSettingsRaw({ ...s, lastExportAt: nowIso() });
}

export function needsExportNag(lastExportAt: string | null, now = Date.now()): boolean {
  if (!lastExportAt) return true;
  return now - Date.parse(lastExportAt) > EXPORT_NAG_DAYS * 86400000;
}

// ---------- import ----------

export interface ImportPreview {
  kind: 'v4' | 'legacy';
  counts: { jobs: number; clients: number; takeoffLines: number; inventoryItems: number; documents: number; photos: number; priceItems: number };
  warnings: string[];
  bundle: Bundle;
}

/** Parse + validate an import file and report what a commit would replace. Throws with a plain-language message. */
export async function buildImportPreview(text: string): Promise<ImportPreview> {
  let obj: unknown;
  try { obj = JSON.parse(text); } catch {
    throw new Error('That file is not readable as a backup file. It should be a .json file exported from Bid Studio.');
  }
  const warnings: string[] = [];
  let bundle: Bundle; let kind: ImportPreview['kind'];
  const anyObj = obj as { app?: string; schema?: number };
  if (anyObj?.app === 'forever-green-bid-studio') {
    kind = 'v4';
    if ((anyObj.schema ?? 0) > SCHEMA_VERSION) {
      throw new Error('This backup came from a newer version of the app. Update the app first, then import.');
    }
    bundle = obj as Bundle;
  } else if (isLegacyFile(obj)) {
    kind = 'legacy';
    bundle = convertLegacy(obj);
    warnings.push('This is a file from the old version. It will be upgraded during import; nothing in the file is changed.');
  } else {
    throw new Error('That file is not a Bid Studio data file.');
  }
  const c = (s: string) => (bundle.data[s] ?? []).length;
  return {
    kind, bundle, warnings,
    counts: {
      jobs: c('jobs'), clients: c('clients'), takeoffLines: c('takeoffLines'),
      inventoryItems: c('inventoryItems'), documents: c('documents'),
      photos: c('photos'), priceItems: c('priceBook'),
    },
  };
}

/** Replace everything with the previewed file. All-or-nothing. */
export async function commitImport(preview: ImportPreview): Promise<void> {
  await restoreBundle(preview.bundle);
}

// ---------- optional auto-export via File System Access (Chromium desktop) ----------

type DirHandle = FileSystemDirectoryHandle & {
  queryPermission?: (o: { mode: string }) => Promise<string>;
  requestPermission?: (o: { mode: string }) => Promise<string>;
};

export function autoExportSupported(): boolean {
  return typeof (globalThis as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function';
}

export async function chooseAutoExportDir(): Promise<boolean> {
  if (!autoExportSupported()) return false;
  const picker = (globalThis as unknown as { showDirectoryPicker: (o: object) => Promise<DirHandle> }).showDirectoryPicker;
  const handle = await picker({ mode: 'readwrite', id: 'fgbs-backup' });
  const db = await getDb();
  await db.put('meta', handle, 'autoExportDir');
  const s = await getSettings();
  await putSettings({ ...s, autoExport: true });
  return true;
}

/** Silent daily write into the chosen folder. Returns false when unavailable — the UI says so plainly. */
export async function runAutoExport(): Promise<boolean> {
  const db = await getDb();
  const handle = (await db.get('meta', 'autoExportDir')) as DirHandle | undefined;
  if (!handle) return false;
  if (handle.queryPermission && (await handle.queryPermission({ mode: 'readwrite' })) !== 'granted') {
    if (!handle.requestPermission || (await handle.requestPermission({ mode: 'readwrite' })) !== 'granted') return false;
  }
  const json = await buildExportJson();
  const file = await handle.getFileHandle(exportFilename(), { create: true });
  const w = await file.createWritable();
  await w.write(json);
  await w.close();
  await markExported();
  return true;
}
